import type { components } from '@fluencelabs/vodopad-admin-api'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Form, Typography } from 'antd'

import { probeSize } from '../api'

const warningLabels: Record<components['schemas']['ProbeWarning'], string> = {
  header_only:
    'Only the file header was inspected; file contents were not verified',
  unknown_format: 'Unknown image format',
  compressed_format:
    'Compressed download; enter the uncompressed virtual capacity',
  external_backing_file: 'The image references an external backing file',
  encryption: 'The image uses encryption',
  incompatible_features: 'The image contains incompatible features',
  unknown_incompatible_features:
    'The image contains unknown incompatible features',
  size_outside_supported_range:
    'The virtual size is outside the supported range',
}

export function SizeProbe() {
  const form = Form.useFormInstance()
  const url = Form.useWatch(['source', 'downloadUrl'], form)
  const probe = useMutation({ mutationFn: probeSize, retry: false })
  const current = probe.variables === url
  const inspect = async () => {
    await form.validateFields([['source', 'downloadUrl']])
    const requestedUrl: string = form.getFieldValue(['source', 'downloadUrl'])
    const previousSize: unknown = form.getFieldValue(['source', 'virtualSize'])
    const result = await probe.mutateAsync(requestedUrl)
    if (
      form.getFieldValue(['source', 'downloadUrl']) === requestedUrl &&
      form.getFieldValue(['source', 'kind']) === 'IMAGE' &&
      form.getFieldValue(['source', 'virtualSize']) === previousSize &&
      result.virtualSizeBytes != null
    ) {
      form.setFieldValue(['source', 'virtualSize'], result.virtualSizeBytes)
    }
  }
  return (
    <>
      <Button
        loading={probe.isPending}
        onClick={() => {
          void inspect().catch(() => undefined)
        }}
      >
        Inspect size from URL
      </Button>
      {current && probe.error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message={probe.error.message}
        />
      )}
      {current && probe.data && (
        <>
          {probe.data.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Inspection warnings: ${probe.data.warnings.map((w) => warningLabels[w]).join('; ')}`}
            />
          )}
          <Typography.Paragraph type="secondary">
            {probe.data.virtualSizeBytes == null
              ? 'Size could not be determined. Enter the virtual capacity manually.'
              : `Detected ${probe.data.virtualSizeBytes} bytes. Review the size field before creating.`}
          </Typography.Paragraph>
        </>
      )}
    </>
  )
}
